<template>
  <div class="bu-files">
    <b-navbar class="bu-files-navbar">
      <b-navbar-brand
        href="#"
        style="display: flex; align-items: center; text-transform: uppercase"
      >
        <b-img
          class="client-program mr-1"
          :src="getProgramImageRoute(1)"
          v-b-tooltip.hover.top
        />

        <span
          :class="isDarkSkin ? 'text-white' : 'text-black'"
          style="font-size: 1.2rem; font-weight: 600; text-transform: uppercase"
        >
          {{ pageTitle }}</span
        >
      </b-navbar-brand>

      <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>
      <b-collapse id="nav-collapse" is-nav>
        <b-navbar-nav class="ml-auto d-flex align-items-center">
          <b-nav-item href="#" class="mr-2">
            <b-form-select
              size="sm"
              v-model="selectedLanguage"
              :options="languageOptions"
              class="language-select"
            ></b-form-select>
          </b-nav-item>

          <b-nav-item href="#" class="mr-2">
            <dark-toggler class="invert" :size="'20'" />
          </b-nav-item>
        </b-navbar-nav>
      </b-collapse>
    </b-navbar>

    <template v-if="isLoading">
      <Skeleton />
    </template>
    <template v-else>
      <b-container
        fluid
        class="pt-3"
        v-if="form && form.files && form.files.length > 0"
      >
        <b-row class="justify-content-start mb-3">
          <b-col cols="12">
            <h4 class="text-left font-weight-bold">
              {{ t.fileDescription }}
            </h4>
          </b-col>
        </b-row>

        <b-row class="justify-content-center mb-1">
          <b-col cols="12">
            <h2 class="text-center font-weight-bold text-uppercase">
              {{ t.company_files_title }}:
              <span class="text-primary">{{ form.c_full_name }}</span>
            </h2>
          </b-col>
        </b-row>

        <b-row v-if="isMobile">
          <b-col v-for="file in form.files" :key="file.id" cols="12">
            <b-card
              class="border rounded-sm p-2 d-flex flex-row align-items-center"
            >
              <div class="flex-shrink-0 mr-3">
                <!-- Imagen -->
                <img
                  v-if="getFilePreviewType(file.ext_file) === 'image'"
                  :src="file.path_file"
                  alt="Preview"
                  class="rounded"
                  style="width: 60px; height: 60px; object-fit: cover"
                />

                <!-- PDF icon -->
                <feather-icon
                  v-else-if="getFilePreviewType(file.ext_file) === 'pdf'"
                  icon="FileText"
                  class="text-danger"
                  size="40"
                />

                <!-- Archivo genérico -->
                <feather-icon
                  v-else
                  icon="File"
                  class="text-secondary"
                  size="40"
                />
              </div>

              <div class="flex-grow-1">
                <div class="text-truncate font-weight-bold">
                  {{ file.file_name }}.{{ file.ext_file }}
                </div>
                <div class="small">
                  {{ getFileType(file.type) }}
                </div>

                <b-button
                  variant="link"
                  class="p-0 small mt-1"
                  :href="file.path_file"
                  target="_blank"
                >
                  <feather-icon icon="Download" size="16" class="mr-1" />
                  {{ t.file.download }}
                </b-button>
              </div>
            </b-card>
          </b-col>
        </b-row>
        <b-row v-else class="justify-content-center">
          <b-col
            v-for="file in form.files"
            :key="file.id"
            cols="12"
            sm="8"
            md="6"
            lg="4"
            xl="3"
          >
            <b-card
              class="shadow-sm border-0 rounded-lg h-100 d-flex flex-column justify-content-between"
              :body-class="'mb-0 pb-0'"
            >
              <div
                v-if="getFilePreviewType(file.ext_file) === 'image'"
                class="mb-1 text-center"
              >
                <img
                  :src="file.path_file"
                  alt="Preview"
                  class="img-fluid rounded"
                  style="max-height: 200px"
                />
              </div>

              <div
                v-else-if="getFilePreviewType(file.ext_file) === 'pdf'"
                class="mb-1"
              >
                <iframe
                  :src="file.path_file"
                  type="application/pdf"
                  width="100%"
                  height="450"
                  style="border: 1px solid #ccc"
                ></iframe>
              </div>

              <div v-else class="mb-1 text-center">
                <feather-icon icon="File" class="text-secondary" size="48" />
                <p class="text-muted mb-0 small">
                  {{ t.preview_not_available }}
                </p>
              </div>

              <div class="d-flex align-items-center mb-1">
                <feather-icon
                  icon="FileText"
                  class="text-primary mr-2"
                  size="20"
                />
                <h5 class="mb-0 text-truncate text-uppercase">
                  {{ getFileType(file.type) }}
                </h5>
              </div>

              <div class="d-flex align-items-center mb-1">
                <feather-icon
                  icon="FileText"
                  class="text-primary mr-2"
                  size="20"
                />
                <h5 class="mb-0 text-truncate text-uppercase">
                  {{ file.file_name }}.{{ file.ext_file }}
                </h5>
              </div>

              <b-button
                class="mt-auto text-uppercase"
                variant="outline-primary"
                :href="file.path_file"
                target="_blank"
                block
              >
                <feather-icon icon="Download" class="mr-2" />
                {{ t.file.download }}
              </b-button>
            </b-card>
          </b-col>
        </b-row>
      </b-container>
      <b-container
        v-else
        class="empty-state d-flex flex-column align-items-center justify-content-center text-center"
      >
        <div class="empty-state__icon">
          <feather-icon icon="FileIcon" variant="danger" size="64" />
        </div>

        <h2 class="empty-state__title">
          {{ t.no_files }}
        </h2>

        <p class="empty-state__description">
          {{ t.no_files_description }}
        </p>

        <div class="empty-state__actions">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="$emit('refresh')"
          >
            <feather-icon icon="RefreshCwIcon" class="mr-1" size="16" />
            {{ t.refresh || "Refresh" }}
          </b-button>
        </div>
      </b-container>
    </template>
  </div>
</template>

<script>
import DarkToggler from "@core/layouts/components/app-navbar/components/DarkToggler.vue";
import translations from "@/views/pages/business-validation-form/data/translation.js";
import BusinessValidationLinkService from "@/views/pages/business-validation-form/service/business.validation.link.js";
import Skeleton from "@/views/pages/business-validation-form/Skeleton.vue";
export default {
  components: {
    DarkToggler,
    Skeleton,
  },
  data() {
    return {
      form: null,
      languageOptions: [
        { value: "es", text: "Español" },
        { value: "en", text: "English" },
      ],
      selectedLanguage: "en",
      isLoading: true,
      isValidLink: false,
      isMobile: window.innerWidth <= 768,
    };
  },
  computed: {
    t() {
      return translations[this.selectedLanguage];
    },
    uniqueCode() {
      return this.$route.params.unique_code;
    },
    pageTitle() {
      return this.t.pageTitle;
    },
  },
  methods: {
    handleResize() {
      this.isMobile = window.innerWidth <= 768;
    },
    async getLinkData() {
      try {
        const { data } = await BusinessValidationLinkService.getLinkData(
          this.uniqueCode
        );
        this.form = data;
        document.title = this.pageTitle;
      } catch (error) {
        const { title, description } = this.t.errorMessage.generalError;
        this.showErrorSwal(description, title);
      } finally {
        this.isLoading = false;
      }
    },
    getFileType(type) {
      switch (type) {
        case "IR":
          return this.t.file.ir;
        case "OA":
          return this.t.file.oa;
        case "EIN":
          return this.t.file.ein;
        case "EVIDENCE":
          return this.t.file.evidence;
        case "ARTICLE_DOCUMENT":
          return this.t.file.articleDocument;
        default:
          return "";
      }
    },
    getFilePreviewType(ext) {
      const imageTypes = ["jpg", "jpeg", "png", "gif", "webp"];
      const pdfTypes = ["pdf"];

      if (imageTypes.includes(ext.toLowerCase())) return "image";
      if (pdfTypes.includes(ext.toLowerCase())) return "pdf";
      return "other";
    },
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },
  created() {
    this.getLinkData();
  },
};
</script>
<style lang="scss" >
@import url("https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap");

.bu-files {
  font-family: "Montserrat", sans-serif !important;

  .validation-form-skeleton {
    margin-top: 5rem;
    padding: 0 1rem;
  }

  &-navbar {
    background-color: #fff;
    box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  }
  .language-select {
    width: auto;
    height: 35px;
    font-size: 1rem;
  }

  .bu-files-navbar .b-navbar-nav {
    display: flex;
    align-items: center;
  }

  .bu-files-navbar .b-nav-item {
    display: flex;
    align-items: center;
  }

  .bu-files-navbar .b-navbar-brand,
  .bu-files-navbar .b-navbar-toggle {
    display: flex;
    align-items: center;
  }

  .empty-state {
    padding: 3rem 1rem;

    &__icon {
      background: rgba(220, 53, 69, 0.1); // light red bg
      border-radius: 50%;
      padding: 1.5rem;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      margin-bottom: 1.5rem;
      transition: transform 0.3s ease;

      &:hover {
        transform: scale(1.05);
      }

      svg {
        stroke-width: 2.5;
      }
    }

    &__title {
      color: #dc3545;
      font-weight: 700;
      font-size: 1.75rem;
      margin-bottom: 0.5rem;
    }

    &__description {
      color: #6c757d;
      font-size: 1.1rem;
      max-width: 500px;
      margin: 0 auto 1.5rem auto;
    }

    &__actions {
      .btn {
        font-weight: 600;
        border-radius: 0.375rem;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
        transition: all 0.2s ease-in-out;

        &:hover {
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        svg {
          margin-top: -2px;
        }
      }
    }
  }
}

.dark-layout {
  .bu-files {
    &-navbar {
      background-color: #17171a !important;
    }

    .invert {
      filter: invert(100%);
    }

    .empty-state {
      &__icon {
        background: rgba(255, 255, 255, 0.1); // light red bg
        svg {
          stroke-width: 2.5;
        }
      }

      &__title {
        color: #fff;
      }

      &__description {
        color: #b0b3b8;
      }
    }
  }
}
</style>
